import { api } from "../lib";
import type {
  AssignPathologistRequest,
  AssignPathologistResponse,
  AssignmentHistoryPage,
  ReassignmentContext,
  ReplacePathologistRequest,
  ReplacePathologistResponse,
} from "../types/casePathologistAssignment";
import type {
  CasePrefixReassignmentContext,
  ReassignCasePrefixRequest,
  ReassignCasePrefixResponse,
} from "../types/casePrefixReassignment";
import type {
  ClientIdentityFields,
  ClientIdentityPreview,
} from "../types/clientIdentity";
import type { FragmentCountQualifier } from "../utils/fragmentCount";
import type { AccessioningPanelSelection } from "./accessioningPanels.service";
import type { WorkListStainSubmission } from "./workList.service";

export type SexType = "M" | "F" | "Other";

export interface PatientAddress {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  sex: SexType;
  medicalRecordNumber: string | null;
  mobilePhone: string | null;
  homePhone: string | null;
  workPhone: string | null;
  email: string | null;
  address: PatientAddress | null;
  createdAt: string;
  updatedAt: string;
}

export interface CaseStatus {
  id: string;
  name: string;
  description: string | null;
}

export interface Workflow {
  id: string;
  name?: string;
  accessioningType?: string;
  description: string | null;
  isActive: boolean;
}

export interface CasePhysician {
  id: string;
  firstName: string;
  lastName: string;
  npi: string | null;
  phone: string | null;
  fax: string | null;
  isActive: boolean;
}

export interface CasePractice {
  id: string;
  name: string;
  shortName: string | null;
  primaryPhone: string | null;
  secondaryPhone: string | null;
  fax: string | null;
  isActive: boolean;
}

export interface CaseSurgeryCenterLab {
  id: string;
  name: string;
  shortName: string | null;
  primaryPhone: string | null;
  fax: string | null;
  isActive: boolean;
}

export interface CaseInsurancePlanType {
  id: string;
  name: string;
  description: string | null;
}

export interface InsuranceInfo {
  insuranceProvider?: string; // ID reference to insurance provider
  planType?: string; // ID reference to insurance plan type
  policyNumber?: string;
  groupNumber?: string;
  groupName?: string;
  authorizationNumber?: string;
  patientRelationToInsured?: string;
  address?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

export interface Case {
  id: string;
  caseNumber: string;
  facilityId: string;
  // Parent case ID for additional stain child cases
  parentCaseId?: string | null;
  parentCase?: {
    id: string;
    caseNumber: string;
  } | null;
  facility?: {
    id: string;
    name: string;
  };
  patientId: string | null;
  patient?: Patient;
  clientId: string | null;
  client?: {
    id: string;
    name: string;
    account?: { id: string; name: string };
    physician?: { id: string; firstName: string; lastName: string };
    location?: { id: string; name: string };
  };
  procedureId: string | null;
  procedureName: string | null;
  procedure?: {
    id: string;
    name: string;
  } | null;
  dateReceived: string;
  dateCollected: string | null;
  clinicalHistoryCodes: string[] | null;
  clinicalHistoryNotes: string | null;
  indications: string | null;
  impressions: string | null;
  primaryInsurance: InsuranceInfo | null;
  secondaryInsurance: InsuranceInfo | null;
  noPatientBillingAndInsuranceInfoRequired: boolean;
  effectiveNoPatientBillingAndInsuranceInfoRequired: boolean;
  primaryInsurancePlanTypeId: string | null;
  primaryInsurancePlanType?: CaseInsurancePlanType | null;
  secondaryInsurancePlanTypeId: string | null;
  secondaryInsurancePlanType?: CaseInsurancePlanType | null;
  pathologistId: string | null;
  pathologistAssignmentRevision?: number;
  pathologist?: {
    id: string;
    fullName: string;
  };
  billingScenarioId: string | null;
  isBlocked: boolean;
  // Rush flag - physical RUSH stamp detected on requisition (expedited processing)
  isRush: boolean;
  rushReason: string | null;
  // High Priority flag - clinical keywords detected (mass, tumor, cancer) (flagged for attention)
  isHighPriority: boolean;
  highPriorityReason: string | null;
  isFinalized: boolean;
  isLegacyImported?: boolean;
  legacyArchiveId?: string | null;
  signedOffAt?: string | null;
  isPartiallyFinalized: boolean;
  hasReportPdf?: boolean;
  workflowId: string | null;
  workflow?: Workflow;
  currentStatusId: string | null;
  currentStatus?: CaseStatus;
  externalCaseNumber: string | null;
  casePrefix?: string | null;
  prefixId?: string | null;
  createdAt: string;
  updatedAt: string;
  // Requisition signal flags (C-4703)
  isCytology?: boolean;
  isResearch?: boolean;
  isConsultation?: boolean;
  isTcOnly?: boolean;
  isGrossingOnly?: boolean;
  // v2 Schema: Direct relationships
  accessioningType?: string;
  sendingPracticeId: string | null;
  sendingPractice?: CasePractice | null;
  physicianId: string | null;
  physician?: CasePhysician | null;
  surgeryCenterLabId: string | null;
  surgeryCenterLab?: CaseSurgeryCenterLab | null;
  submittingLabId: string | null;
  submittingLab?: CaseSurgeryCenterLab | null;
  // For pass-through cases
  originatingPracticeId: string | null;
  originatingPractice?: CasePractice | null;
  originatingPhysicianId: string | null;
  originatingPhysician?: CasePhysician | null;
  originatingSurgeryCenterId: string | null;
  originatingSurgeryCenter?: CaseSurgeryCenterLab | null;
  // For consultation cases
  consultationReason: string | null;
  consultationOriginalDiagnosis: string | null;
  consultationClinicalContext: string | null;
  requestingPathologist: string | null;
  // Created by user
  createdById: string | null;
  createdBy?: { id: string; fullName: string } | null;
  // AI extraction snapshot and draft UI state
  aiExtractedData?: {
    draftUiState?: {
      slideCompletions?: Record<string, Record<string, boolean>>;
      manualStains?: Record<
        number,
        {
          added: Array<{
            id: string;
            selectionId?: string;
            code: string;
            isSystemGenerated?: boolean;
          }>;
          removed: string[];
        }
      >;
      panelSelections?: Record<number, AccessioningPanelSelection[]>;
    };
    [key: string]: any;
  } | null;
  // Draft patient data - stores partial patient info before all required fields are filled
  draftPatientData?: {
    firstName?: string | null;
    lastName?: string | null;
    dateOfBirth?: string | null;
    sex?: string | null;
    medicalRecordNumber?: string | null;
    mobilePhone?: string | null;
    homePhone?: string | null;
    workPhone?: string | null;
    email?: string | null;
    address?: Record<string, any> | null;
  } | null;
  // Sales Rep tracking - case came through a sales representative
  isThroughSalesRep: boolean;
  salesRepId: string | null;
  salesRep?: {
    id: string;
    name: string;
  } | null;
  // TC shipping destination
  shippingPathologist?: string | null;
  shippingStreet?: string | null;
  shippingCity?: string | null;
  shippingState?: string | null;
  shippingZipCode?: string | null;
  // Specimen and block counts - populated from backend when fetching list
  specimenCount?: number;
  blockCount?: number;
  // Lock status - populated from backend when fetching list
  lockedBy?: string | null;
  lockedByName?: string | null;
}

export interface CaseSlide {
  id: string;
  caseId: string;
  specimenId: string;
  blockId: string | null;
  number: string;
  physicalSlideNumber?: number | null;
  stainId: string | null;
  stain?: {
    id: string;
    code: string;
    description: string;
  } | null;
  isCompleted: boolean;
  isControlSlide?: boolean;
  controlForPracticeId?: string | null;
  barcode: string | null;
  status: string;
  createdAt: string;
  printedAt?: string | null;
  lastStickerPrintedAt?: string | null;
}

export interface CaseBlock {
  id: string;
  caseId: string;
  specimenId: string;
  number: string;
  isAdditional: boolean;
  createdById: string | null;
  createdAt: string;
  embeddedAt?: string | null;
  embeddedById?: string | null;
  slides?: CaseSlide[];
  isCompleted?: boolean;
  lastStickerPrintedAt?: string | null;
}

export interface CaseSpecimen {
  id: string;
  caseId: string;
  number: string;
  sourceId: string | null;
  source?: {
    id: string;
    name: string;
  } | null;
  subsiteId: string | null;
  subsite?: {
    id: string;
    name: string;
  } | null;
  collectionMethod: string | null;
  material: string | null;
  findings: string | null;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  ruleOuts: string[] | null;
  comments: string | null;
  gross: string | null;
  accessioningGrossCodeId: string | null;
  internalGrossCodeId: string | null;
  tissueDisposition: string | null;
  grossedById: string | null;
  grossedBy?: {
    id: string;
    fullName: string;
  } | null;
  grossedAt: string | null;
  createdAt: string;
  updatedAt: string;
  blocks?: CaseBlock[];
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CaseFilters {
  statusIds?: string[];
  statusNames?: string[];
  facilityIds?: string[];
  clientId?: string;
  sendingPracticeId?: string[];
  physicianId?: string;
  submittingLabId?: string[];
  surgeryCenterLabId?: string[];
  pathologistIds?: string[];
  currentStatusIds?: string[];
  workflowIds?: string[];
  procedureIds?: string[];
  patientIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  dateCollected?: string;
  search?: string;
  isBlocked?: boolean;
  isRush?: boolean;
  isHighPriority?: boolean;
  isAdditionalStain?: boolean;
  // Exclude draft cases with no data (defaults to true on backend)
  excludeEmptyDrafts?: boolean;
  // Return ONLY empty draft cases (for "Missed Cases" tab)
  onlyEmptyDrafts?: boolean;
  // Case flags filter (AND logic — cases must have ALL specified flags)
  flagDefinitionIds?: string[];
  prefixKeys?: string[];
  // Client-side only fields (not sent to backend)
  caseType?: string;
  tatOverdue?: boolean;
  tatMinDays?: number;
  hasPendingTests?: boolean;
  currentStep?: string;
  eligibleForSendOut?: boolean;
  sortBy?: "caseNumber";
  sortDirection?: "asc" | "desc";
}

export type CaseAdjacentDirection = "previous" | "next";

export interface CaseAdjacentResponse {
  adjacentCaseId: string | null;
}

export interface CaseStats {
  total: number;
  blocked: number;
  byStatus: Array<{
    statusId: string;
    statusName: string;
    count: number;
  }>;
  byWorkflow: Array<{
    workflowId: string;
    workflowName: string;
    count: number;
  }>;
}

export interface DashboardStageCounts {
  stage: string;
  total: number;
  blocked: number;
  withErrors: number;
  blockedWithErrors: number;
}

export interface DashboardStats {
  casesToday: number;
  completed: number;
  blocked: number;
  pendingErrorCount: number;
  averageTatDays: number;
  agedCaseCount: number;
  stageCounts: DashboardStageCounts[];
}

export interface RequestedStainTarget {
  stainCode: string;
  specimenNumber: string;
  blockNumber: string | null;
  quantity: number;
}

export interface CaseStatusHistoryItem {
  id: string;
  caseId: string;
  eventType:
    | "status"
    | "additional_stain_request"
    | "additional_stain_milestone"
    | "correction";
  title: string;
  fromStatusId?: string;
  fromStatus?: CaseStatus;
  toStatusId?: string;
  toStatus?: CaseStatus;
  changedById?: string;
  changedBy?: { id: string; fullName: string };
  changedAt: string;
  requestedItems?: RequestedStainTarget[];
}

export interface CaseFile {
  id: string;
  caseId: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  // Upload source — which screen the file was uploaded from.
  // 'accessioning' | 'pathology_review' | 'case_details'
  documentType?: string;
  label?: string | null;
  description?: string | null;
  includeInReport: boolean;
  finalizedInReport: boolean;
  createdAt: string;
}

export interface CaseErrorReport {
  id: string;
  caseId: string;
  errorId: string;
  error?: { id: string; error: string; category: string };
  discrepancy?: { id: string; error: string; category: string };
  description: string;
  reportedById?: string;
  reportedBy?: { id: string; fullName: string };
  reportedAt: string;
  resolvedById?: string;
  resolvedBy?: { id: string; fullName: string };
  resolvedAt?: string;
  resolutionNotes?: string;
  correctionRequestId?: string | null;
  correctionRequest?: { id?: string; refNumber?: number };
  refNumber?: number;
  isResolved?: boolean;
  status?: "pending" | "resolved";
}

export interface CaseEmail {
  id: string;
  caseId: string;
  subject: string;
  body: string;
  toAddresses: string[];
  ccAddresses?: string[];
  sentById?: string;
  sentBy?: { id: string; fullName: string };
  sentAt: string;
  status: string;
}

export interface UnifiedCommunication {
  id: string;
  type: "call" | "email";
  contactPerson?: string;
  subject?: string;
  notes: string;
  loggedBy: { id: string; fullName: string } | null;
  createdAt: string;
  source: "case" | "send-out";
  sendOutLabel?: string | null;
}

export interface CaseQaReview {
  id: string;
  caseId: string | null;
  sendOutId: string;
  originalPathologistId: string | null;
  reviewedById: string;
  status: string;
  agreementLevel: string | null;
  recommendedAction: string;
  internalDiagnosisSummary: string | null;
  externalDiagnosisSummary: string | null;
  discrepancies: string | null;
  comments: string | null;
  actionTaken: string | null;
  actionTakenById: string | null;
  actionTakenAt: string | null;
  reviewedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reviewedBy?: { id: string; fullName: string };
  actionTakenBy?: { id: string; fullName: string } | null;
  originalPathologist?: { id: string; fullName: string } | null;
  sendOut?: {
    id: string;
    externalInstitution?: { id: string; name: string } | null;
  } | null;
}

export interface CreateCaseDto {
  facilityId: string;
  patientId?: string;
  clientId?: string;
  procedureName?: string;
  dateReceived: string;
  dateCollected?: string;
  clinicalHistoryCodes?: string[];
  clinicalHistoryNotes?: string;
  primaryInsurance?: Record<string, any>;
  secondaryInsurance?: Record<string, any>;
  pathologistId?: string;
  billingScenarioId?: string;
  workflowId?: string;
  externalCaseNumber?: string;
  // Rush flag - physical RUSH stamp detected on requisition (expedited processing)
  isRush?: boolean;
  rushReason?: string;
  // High Priority flag - clinical keywords detected (mass, tumor, cancer) (flagged for attention)
  isHighPriority?: boolean;
  highPriorityReason?: string;
  // Sales Rep tracking
  isThroughSalesRep?: boolean;
  salesRepId?: string;
}

export interface UpdateCaseDto extends Partial<CreateCaseDto> {
  currentStatusId?: string;
}

export interface CreateSpecimenDto {
  number: string;
  sourceId?: string;
  subsiteId?: string;
  collectionMethod?: string;
  ruleOuts?: string[];
  comments?: string;
  gross?: string;
  accessioningGrossCodeId?: string;
}

export interface UpdateSpecimenDto extends Partial<CreateSpecimenDto> {
  internalGrossCodeId?: string;
  tissueDisposition?: string;
}

/**
 * Request for generating cassette labels
 */
export interface GenerateCassetteLabelsRequest {
  specimens: Array<{
    specimenId?: string;
    specimenLetter: string;
    blockCount: number;
  }>;
  // Effective prefix the user sees on screen (manual override or current
  // lookup result). Authoritative on the backend so cassette barcodes match
  // what the user is looking at, regardless of autosave timing.
  casePrefix?: string;
}

/**
 * Request to generate accessioning-time sticker labels. Keys mirror the
 * frontend's local completion state: slideCompletions and manualStains are
 * keyed by the specimen's array index.
 */
export interface GenerateStickerLabelsRequest {
  specimens: Array<{
    number: string;
    expectedSpecimenNumber?: string;
    blockCount: number;
    material?: string | null;
  }>;
  slideCompletions: Record<string, Record<string, boolean>>;
  manualStains?: Record<
    string,
    {
      added: Array<{ id: string; selectionId?: string; code: string }>;
      removed: string[];
    }
  >;
  panelSelections?: Record<string, AccessioningPanelSelection[]>;
  workListStains?: WorkListStainSubmission[];
  /** Final stain list visible to the user. Used verbatim for sticker planning. */
  resolvedStains?: Record<
    string,
    Array<{ stainId: string; selectionId?: string; isCompleted: boolean }>
  >;
  expectedCaseId?: string;
}

/**
 * One completed slide in the sticker-label plan. Numbering matches what the
 * slide will be assigned at completion, so the printed barcode stays valid.
 */
export interface StickerLabelPlanItem {
  specimenNumber: string;
  blockNumber: string;
  slideNumber: string;
  stainCode: string;
  stainId: string;
}

/**
 * Cassette label data structure for printing
 * Format: {caseNumber};{specimenLetter};{blockNumber}
 */
export interface CassetteLabel {
  blockId: string;
  barcode: string; // Format: "G25-01234;A;1"
  caseNumber: string;
  specimenLetter: string;
  blockNumber: string;
  patientName: string; // Format: "LastName, FirstName"
  facilityName: string;
}

/**
 * Case note data structure
 */
export interface CaseNote {
  id: string;
  caseId: string;
  noteText: string;
  caseStatusId: string | null;
  caseStatus?: CaseStatus;
  createdById: string;
  createdBy?: {
    id: string;
    fullName: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Additional stain info from child cases
 */
export interface AdditionalStainInfo {
  id: string;
  childCaseId: string;
  childCaseNumber: string;
  workflowStatus: string;
  stain: {
    id: string;
    code: string;
    description: string;
    stainType: string;
  };
  specimen: {
    id: string;
    number: string;
    source?: string;
  };
  block: {
    id: string;
    number: string;
  };
  provider: {
    id: string | null;
    name: string;
    isInHouse: boolean;
  };
  levelsRequested: string | null;
  requestedBy: {
    id: string;
    name: string;
  };
  requestedAt: string;
  createdAt: string;
  isCompleted: boolean;
}

const CASE_STATS_FILTER_KEYS: ReadonlyArray<keyof CaseFilters> = [
  "facilityIds",
  "sendingPracticeId",
  "physicianId",
  "submittingLabId",
  "surgeryCenterLabId",
  "pathologistIds",
  "currentStatusIds",
  "workflowIds",
  "procedureIds",
  "patientIds",
  "dateFrom",
  "dateTo",
  "dateCollected",
  "search",
  "isBlocked",
  "isRush",
  "isHighPriority",
  "excludeEmptyDrafts",
  "onlyEmptyDrafts",
  "flagDefinitionIds",
  "eligibleForSendOut",
];

export function getCaseStatsFilters(filters: CaseFilters): CaseFilters {
  return Object.fromEntries(
    CASE_STATS_FILTER_KEYS.flatMap((key) =>
      filters[key] === undefined ? [] : [[key, filters[key]]]
    )
  ) as CaseFilters;
}

function createCaseFilterSearchParams(filters: CaseFilters): URLSearchParams {
  const params = new URLSearchParams();
  appendCaseFilters(params, filters);
  return params;
}

function createPaginatedCaseSearchParams(
  filters: CaseFilters,
  page: number,
  limit: number
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("page", page.toString());
  params.set("limit", limit.toString());
  appendCaseFilters(params, filters);
  return params;
}

export function appendCaseFilters(
  params: URLSearchParams,
  filters: CaseFilters
): void {
  Object.entries(filters).forEach(([key, value]) =>
    appendCaseFilter(params, key, value)
  );
}

const CASE_LIST_CONTEXT_KEYS: ReadonlyArray<keyof CaseFilters> = [
  "search",
  "facilityIds",
  "sendingPracticeId",
  "physicianId",
  "submittingLabId",
  "surgeryCenterLabId",
  "pathologistIds",
  "currentStatusIds",
  "workflowIds",
  "procedureIds",
  "patientIds",
  "dateFrom",
  "dateTo",
  "dateCollected",
  "isBlocked",
  "isRush",
  "isHighPriority",
  "flagDefinitionIds",
  "eligibleForSendOut",
  "sortBy",
  "sortDirection",
];

function selectCaseListContext(filters: CaseFilters): CaseFilters {
  return Object.fromEntries(
    CASE_LIST_CONTEXT_KEYS.flatMap((key) =>
      filters[key] === undefined ? [] : [[key, filters[key]]]
    )
  ) as CaseFilters;
}

export function createCaseListNavigationParams(
  filters: CaseFilters
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("listContext", "cases");
  appendCaseFilters(params, selectCaseListContext(filters));
  return params;
}

function readCaseListContextArray(
  params: URLSearchParams,
  key: string
): string[] | undefined {
  const value = params.get(key);
  return value ? value.split(",").filter(Boolean) : undefined;
}

function readCaseListContextBoolean(
  params: URLSearchParams,
  key: string
): boolean | undefined {
  const value = params.get(key);
  if (value === "1" || value === "true") return true;
  return undefined;
}

export function parseCaseListNavigationContext(
  params: URLSearchParams
): CaseFilters | null {
  if (params.get("listContext") !== "cases") return null;
  return {
    search: params.get("search") || undefined,
    facilityIds: readCaseListContextArray(params, "facilityIds"),
    sendingPracticeId: readCaseListContextArray(params, "sendingPracticeId"),
    physicianId: params.get("physicianId") || undefined,
    submittingLabId: readCaseListContextArray(params, "submittingLabId"),
    surgeryCenterLabId: readCaseListContextArray(params, "surgeryCenterLabId"),
    pathologistIds: readCaseListContextArray(params, "pathologistIds"),
    currentStatusIds: readCaseListContextArray(params, "currentStatusIds"),
    workflowIds: readCaseListContextArray(params, "workflowIds"),
    procedureIds: readCaseListContextArray(params, "procedureIds"),
    patientIds: readCaseListContextArray(params, "patientIds"),
    dateFrom: params.get("dateFrom") || undefined,
    dateTo: params.get("dateTo") || undefined,
    dateCollected: params.get("dateCollected") || undefined,
    isBlocked: readCaseListContextBoolean(params, "isBlocked"),
    isRush: readCaseListContextBoolean(params, "isRush"),
    isHighPriority: readCaseListContextBoolean(params, "isHighPriority"),
    flagDefinitionIds: readCaseListContextArray(params, "flagDefinitionIds"),
    eligibleForSendOut: readCaseListContextBoolean(
      params,
      "eligibleForSendOut"
    ),
    sortBy: params.get("sortBy") === "caseNumber" ? "caseNumber" : undefined,
    sortDirection:
      params.get("sortDirection") === "asc"
        ? "asc"
        : params.get("sortDirection") === "desc"
        ? "desc"
        : undefined,
  };
}

function appendCaseFilter(
  params: URLSearchParams,
  key: string,
  value: unknown
): void {
  if (value === undefined || value === "") return;
  if (Array.isArray(value)) {
    if (value.length > 0) params.set(key, value.join(","));
    return;
  }
  params.set(key, String(value));
}

export const casesService = {
  getAll: async (
    filters: CaseFilters = {},
    page = 1,
    limit = 50
  ): Promise<PaginatedResponse<Case>> => {
    const params = createPaginatedCaseSearchParams(filters, page, limit);
    return api.get<PaginatedResponse<Case>>(`/cases?${params.toString()}`);
  },

  getAdjacent: async (
    caseId: string,
    direction: CaseAdjacentDirection,
    filters: CaseFilters
  ): Promise<CaseAdjacentResponse> => {
    const params = createCaseFilterSearchParams(selectCaseListContext(filters));
    params.set("direction", direction);
    return api.get<CaseAdjacentResponse>(
      `/cases/${caseId}/adjacent?${params.toString()}`
    );
  },

  /**
   * Get prior cases for the same patient (matched by MRN or Name+DOB).
   * Uses a dedicated endpoint that handles patient matching server-side.
   */
  getPriorCases: async (
    caseId: string,
    limit = 50
  ): Promise<PaginatedResponse<Case>> => {
    const params = new URLSearchParams();
    params.set("limit", limit.toString());
    return api.get<PaginatedResponse<Case>>(
      `/cases/${caseId}/prior-cases?${params.toString()}`
    );
  },

  /**
   * Get dashboard statistics (cases today, completed, blocked)
   */
  getDashboardStats: async (): Promise<DashboardStats> => {
    return api.get<DashboardStats>("/cases/stats");
  },

  getById: async (id: string): Promise<Case> => {
    return api.get<Case>(`/cases/${id}`);
  },

  getByCaseNumber: async (caseNumber: string): Promise<Case | null> => {
    return api.get<Case | null>(`/cases/by-number/${caseNumber}`);
  },

  getPathologistReassignmentContext: async (
    caseId: string
  ): Promise<ReassignmentContext> => {
    return api.get<ReassignmentContext>(
      `/cases/${caseId}/pathologist-reassignment-context`
    );
  },

  getPrefixReassignmentContext: async (
    caseId: string
  ): Promise<CasePrefixReassignmentContext> => {
    return api.get<CasePrefixReassignmentContext>(
      `/cases/${caseId}/prefix-reassignment-context`
    );
  },

  reassignCasePrefix: async (
    caseId: string,
    request: ReassignCasePrefixRequest
  ): Promise<ReassignCasePrefixResponse> => {
    return api.post<ReassignCasePrefixResponse>(
      `/cases/${caseId}/prefix-reassignments`,
      request
    );
  },

  replaceCasePathologist: async (
    caseId: string,
    request: ReplacePathologistRequest
  ): Promise<ReplacePathologistResponse> => {
    return api.post<ReplacePathologistResponse>(
      `/cases/${caseId}/pathologist-reassignments`,
      request
    );
  },

  assignCasePathologist: async (
    caseId: string,
    request: AssignPathologistRequest
  ): Promise<AssignPathologistResponse> => {
    return api.post<AssignPathologistResponse>(
      `/cases/${caseId}/pathologist-assignments`,
      request
    );
  },

  getCasePathologistAssignmentHistory: async (
    caseId: string,
    page = 1,
    limit = 50
  ): Promise<AssignmentHistoryPage> => {
    return api.get<AssignmentHistoryPage>(
      `/cases/${caseId}/pathologist-assignment-history?page=${page}&limit=${limit}`
    );
  },

  create: async (data: CreateCaseDto): Promise<Case> => {
    return api.post<Case>("/cases", data);
  },

  update: async (id: string, data: UpdateCaseDto): Promise<Case> => {
    return api.put<Case>(`/cases/${id}`, data);
  },

  updateStatus: async (
    id: string,
    statusId: string,
    notes?: string
  ): Promise<Case> => {
    return api.patch<Case>(`/cases/${id}/status`, { statusId, notes });
  },

  toggleBlocked: async (id: string): Promise<Case> => {
    return api.patch<Case>(`/cases/${id}/toggle-blocked`);
  },

  delete: async (id: string): Promise<void> => {
    return api.delete<void>(`/cases/${id}`);
  },

  /**
   * Discard an empty draft case (guarded endpoint — only truly empty drafts).
   * Used by the Missed Cases tab and the navigate-away discard prompt.
   */
  discardDraft: async (id: string): Promise<void> => {
    return api.delete<void>(`/cases/${id}/discard-draft`);
  },

  // Specimens
  getSpecimens: async (caseId: string): Promise<CaseSpecimen[]> => {
    return api.get<CaseSpecimen[]>(`/cases/${caseId}/specimens`);
  },

  createSpecimen: async (
    caseId: string,
    data: CreateSpecimenDto
  ): Promise<CaseSpecimen> => {
    return api.post<CaseSpecimen>(`/cases/${caseId}/specimens`, data);
  },

  updateSpecimen: async (
    caseId: string,
    specimenId: string,
    data: UpdateSpecimenDto
  ): Promise<CaseSpecimen> => {
    return api.put<CaseSpecimen>(
      `/cases/${caseId}/specimens/${specimenId}`,
      data
    );
  },

  deleteSpecimen: async (caseId: string, specimenId: string): Promise<void> => {
    return api.delete<void>(`/cases/${caseId}/specimens/${specimenId}`);
  },

  // Blocks
  getBlocks: async (caseId: string): Promise<CaseBlock[]> => {
    return api.get<CaseBlock[]>(`/cases/${caseId}/blocks`);
  },

  createBlock: async (
    caseId: string,
    data: Partial<CaseBlock>
  ): Promise<CaseBlock> => {
    return api.post<CaseBlock>(`/cases/${caseId}/blocks`, data);
  },

  deleteBlock: async (caseId: string, blockId: string): Promise<void> => {
    return api.delete<void>(`/cases/${caseId}/blocks/${blockId}`);
  },

  // Slides
  getSlides: async (caseId: string): Promise<CaseSlide[]> => {
    return api.get<CaseSlide[]>(`/cases/${caseId}/slides`);
  },

  createSlide: async (
    caseId: string,
    data: Partial<CaseSlide>
  ): Promise<CaseSlide> => {
    return api.post<CaseSlide>(`/cases/${caseId}/slides`, data);
  },

  updateSlideStatus: async (
    caseId: string,
    slideId: string,
    status: string
  ): Promise<CaseSlide> => {
    return api.patch<CaseSlide>(`/cases/${caseId}/slides/${slideId}/status`, {
      status,
    });
  },

  deleteSlide: async (caseId: string, slideId: string): Promise<void> => {
    return api.delete<void>(`/cases/${caseId}/slides/${slideId}`);
  },

  // Cassette Labels (for printing)
  // NOTE: Actual Thermo PrintMate printer integration is mocked for now

  /**
   * Generate cassette labels for printing
   * @param caseId - The case ID
   * @param request - Specimen block count information
   * @returns Array of cassette labels ready for printing
   */
  generateCassetteLabels: async (
    caseId: string,
    request: GenerateCassetteLabelsRequest
  ): Promise<CassetteLabel[]> => {
    return api.post<CassetteLabel[]>(
      `/cases/${caseId}/cassette-labels`,
      request
    );
  },

  /**
   * Generate the sticker-label plan for a case still being accessioned.
   * Slides/blocks are not persisted until completion, so the backend resolves
   * the specimen/block/slide numbering (matching what completion will assign)
   * for completed slides only, without persisting anything.
   */
  generateStickerLabels: async (
    caseId: string,
    request: GenerateStickerLabelsRequest
  ): Promise<StickerLabelPlanItem[]> => {
    return api.post<StickerLabelPlanItem[]>(
      `/cases/${caseId}/sticker-labels`,
      request
    );
  },

  // Case Notes

  /**
   * Get all notes for a case
   */
  getNotes: async (caseId: string): Promise<CaseNote[]> => {
    return api.get<CaseNote[]>(`/cases/${caseId}/notes`);
  },

  /**
   * Create a new note for a case
   */
  createNote: async (
    caseId: string,
    noteText: string,
    caseStatusId?: string
  ): Promise<CaseNote> => {
    return api.post<CaseNote>(`/cases/${caseId}/notes`, {
      noteText,
      caseStatusId,
    });
  },

  /**
   * Update a note
   */
  updateNote: async (
    caseId: string,
    noteId: string,
    noteText: string
  ): Promise<CaseNote> => {
    return api.put<CaseNote>(`/cases/${caseId}/notes/${noteId}`, { noteText });
  },

  /**
   * Delete a note
   */
  deleteNote: async (caseId: string, noteId: string): Promise<void> => {
    return api.delete<void>(`/cases/${caseId}/notes/${noteId}`);
  },

  /** Get filtered case statistics from the backend aggregate endpoint. */
  getCaseStats: async (filters: CaseFilters = {}): Promise<CaseStats> => {
    const query = createCaseFilterSearchParams(
      getCaseStatsFilters(filters)
    ).toString();
    const endpoint = query ? `/cases/list-stats?${query}` : "/cases/list-stats";
    return api.get<CaseStats>(endpoint);
  },

  // Case Status History
  /**
   * Get status change history for a case
   * Note: Backend endpoint may not be implemented yet
   */
  getStatusHistory: async (
    caseId: string
  ): Promise<CaseStatusHistoryItem[]> => {
    try {
      return await api.get<CaseStatusHistoryItem[]>(
        `/cases/${caseId}/status-history`
      );
    } catch {
      // Return empty array if endpoint not available
      return [];
    }
  },

  // Case Files
  /**
   * Get files attached to a case, optionally filtered by document type
   */
  getFiles: async (
    caseId: string,
    documentType?: string
  ): Promise<CaseFile[]> => {
    try {
      const params = documentType ? `?documentType=${documentType}` : "";
      return await api.get<CaseFile[]>(`/cases/${caseId}/files${params}`);
    } catch {
      // Return empty array if endpoint not available
      return [];
    }
  },

  /** Fetch report attachments without concealing request failures. */
  getReportFiles: (caseId: string): Promise<CaseFile[]> =>
    api.get<CaseFile[]>(`/cases/${caseId}/files`),

  /**
   * Save uploaded documents to a case
   */
  saveDocuments: async (
    caseId: string,
    documents: Array<{
      s3Key: string;
      fileName?: string;
      mimeType?: string;
      size?: number;
    }>,
    documentType = "accessioning"
  ): Promise<CaseFile[]> => {
    return api.post<CaseFile[]>(`/cases/${caseId}/files`, {
      documents,
      documentType,
    });
  },

  /**
   * Toggle includeInReport on a case file
   */
  toggleFileIncludeInReport: async (
    caseId: string,
    fileId: string
  ): Promise<CaseFile> => {
    return api.patch<CaseFile>(
      `/cases/${caseId}/files/${fileId}/toggle-include`
    );
  },

  /**
   * Delete a file from a case
   */
  deleteFile: async (caseId: string, fileId: string): Promise<void> => {
    return api.delete(`/cases/${caseId}/files/${fileId}`);
  },

  /**
   * Update editable metadata (label, description) on a case file.
   */
  updateFile: async (
    caseId: string,
    fileId: string,
    payload: { label?: string | null; description?: string | null }
  ): Promise<CaseFile> => {
    return api.patch<CaseFile>(`/cases/${caseId}/files/${fileId}`, payload);
  },

  // Case Error Reports
  /**
   * Get error reports for a case
   */
  getErrorReports: async (caseId: string): Promise<CaseErrorReport[]> => {
    try {
      return await api.get<CaseErrorReport[]>(
        `/workflows/errors/case/${caseId}`
      );
    } catch {
      // Return empty array if endpoint not available
      return [];
    }
  },

  // Case Email Communications
  /**
   * Get email communications for a case
   * Note: Backend endpoint may not be implemented yet
   */
  getEmails: async (caseId: string): Promise<CaseEmail[]> => {
    try {
      return await api.get<CaseEmail[]>(`/cases/${caseId}/emails`);
    } catch {
      // Return empty array if endpoint not available
      return [];
    }
  },

  // Case Communications (unified: case-level + send-out follow-ups)
  getCommunications: async (
    caseId: string
  ): Promise<UnifiedCommunication[]> => {
    return api.get<UnifiedCommunication[]>(`/cases/${caseId}/communications`);
  },

  addCommunicationLog: async (
    caseId: string,
    data: {
      type: "call" | "email";
      contactPerson?: string;
      subject?: string;
      notes: string;
    }
  ): Promise<UnifiedCommunication> => {
    return api.post<UnifiedCommunication>(
      `/cases/${caseId}/communications`,
      data
    );
  },

  /**
   * Get additional stains (child cases) for a parent case.
   * Returns both active and completed child cases with workflow status.
   */
  getAdditionalStains: async (
    caseId: string
  ): Promise<AdditionalStainInfo[]> => {
    try {
      return await api.get<AdditionalStainInfo[]>(
        `/cases/${caseId}/additional-stains`
      );
    } catch {
      // Return empty array if endpoint not available
      return [];
    }
  },

  getQaReviews: async (caseId: string): Promise<CaseQaReview[]> => {
    try {
      return await api.get<CaseQaReview[]>(`/cases/${caseId}/qa-reviews`);
    } catch {
      return [];
    }
  },

  /**
   * Resolve what a set of candidate client values would mean for a case's prefix, case number
   * and billing scenario. Read-only — it allocates nothing and reserves nothing, so the
   * proposed case number is advisory until the change is saved.
   *
   * The candidate is sent as given: an omitted key means "unchanged from the case", an explicit
   * `null` means "cleared". Do not normalise one into the other.
   */
  previewClientIdentity: async (
    caseId: string,
    candidate: ClientIdentityFields
  ): Promise<ClientIdentityPreview> => {
    return api.post<ClientIdentityPreview>(
      `/cases/${caseId}/client-identity-preview`,
      candidate
    );
  },
};
